// Create Match page (/admin/matches/new[?season=<id>]) - replaces the Create Match modal of
// Matches.js. Sections on the left, a live preview with the Create button on the right.
// Form rules + POST body: ./matchForm.js (unchanged from the modal). After creating:
// veto → VetoLinksModal; else the setup timeline (match_setup_progress over /ws) → match page.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import Select from 'react-select';
import { useTranslation } from 'react-i18next';
import AnimSelect from '../AnimSelect';
import TeamLogo from '../teams/TeamLogo';
import { useAuth } from '../../context/AuthContext';
import { AlertModal } from '../AlertModal';
import VetoLinksModal from '../matches/VetoLinksModal';
import MatchSetupProgress from './MatchSetupProgress';
import SeatingStep, { seatingComplete } from './SeatingStep';
import {
  emptyMatchForm, withBestOf, applySeason, ongoingSeasons, vetoPoolCheck, validate, buildPayload, vetoFormat, shortMap, POOLS,
} from './matchForm';
import './createMatch.css';

const STATUS_KEYS = ['available', 'busy', 'offline']; // .cmp-status.<key> colours

const selectStyles = (hasError) => ({
  control: (base, state) => ({
    ...base, background: 'var(--bg-input)', minHeight: 40, borderRadius: 9,
    borderColor: hasError ? 'rgba(239,68,68,0.7)' : state.isFocused ? 'rgba(59,130,246,0.6)' : 'var(--border-subtle)',
    boxShadow: state.isFocused ? '0 0 0 1px rgba(59,130,246,0.35)' : 'none',
    '&:hover': { borderColor: 'var(--border-hover)' },
  }),
  menu: (base) => ({ ...base, background: 'var(--bg-elevated)', border: '1px solid var(--border-default)', borderRadius: 10, boxShadow: '0 12px 36px rgba(0,0,0,0.5)', zIndex: 100, overflow: 'hidden' }),
  menuList: (base) => ({ ...base, maxHeight: 300 }),
  option: (base, state) => ({
    ...base, cursor: state.isDisabled ? 'not-allowed' : 'pointer', padding: '7px 10px', opacity: state.isDisabled ? 0.4 : 1,
    backgroundColor: state.isSelected ? 'rgba(59,130,246,0.2)' : state.isFocused ? 'var(--bg-card-hover)' : 'transparent',
    color: 'var(--text-primary)',
  }),
  singleValue: (base) => ({ ...base, color: 'var(--text-primary)' }),
  input: (base) => ({ ...base, color: 'var(--text-primary)' }),
  placeholder: (base) => ({ ...base, color: 'var(--text-muted)' }),
});

function Switch({ on }) { return <span className={`cmp-switch${on ? ' on' : ''}`} aria-hidden="true" />; }

function Toggle({ on, label, hint, disabled, onChange }) {
  return (
    <button type="button" role="switch" aria-checked={!!on} className={`cmp-toggle${on ? ' on' : ''}${disabled ? ' disabled' : ''}`}
      onClick={() => !disabled && onChange(!on)} disabled={disabled}>
      <span><span className="cmp-toggle-label">{label}</span>{hint && <span className="cmp-toggle-hint">{hint}</span>}</span>
      <Switch on={on} />
    </button>
  );
}

function Section({ no, done, title, hint, extra, error, innerRef, children }) {
  return (
    <section className={`cmp-sec${error ? ' err' : ''}`} ref={innerRef}>
      <div className="cmp-sec-head">
        <span className={`cmp-sec-no${done ? ' done' : ''}`}>{done ? '✓' : no}</span>
        <div><div className="cmp-sec-title">{title}</div>{hint && <div className="cmp-sec-hint">{hint}</div>}</div>
        {extra && <div className="cmp-sec-extra">{extra}</div>}
      </div>
      <div className="cmp-sec-body">{children}</div>
    </section>
  );
}

export default function CreateMatchPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [params] = useSearchParams();

  // data
  const [teams, setTeams] = useState([]);
  const [servers, setServers] = useState([]);
  const [mapDict, setMapDict] = useState([]);
  const [seasons, setSeasons] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [loadError, setLoadError] = useState('');

  // form
  const [form, setForm] = useState(emptyMatchForm);
  const [mapMode, setMapMode] = useState('active_duty');
  const [seasonId, setSeasonId] = useState('');
  const [errors, setErrors] = useState({});
  const [error, setError] = useState('');
  const [activeSlot, setActiveSlot] = useState(0);
  const [serverQuery, setServerQuery] = useState('');
  const [showAllServers, setShowAllServers] = useState(false);     // past the first SERVER_LIMIT
  const [showOtherServers, setShowOtherServers] = useState(false); // super admin: other admins' servers
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [addingDefaults, setAddingDefaults] = useState(false);
  const [alert, setAlert] = useState(null);

  // create
  const [creating, setCreating] = useState(false);
  const [seating, setSeating] = useState(null); // optional stage seating (null = the teams' defaults)
  const [progress, setProgress] = useState({ step: 0, label: '', hasBackup: true, serverInfo: null });
  const [vetoResult, setVetoResult] = useState(null);
  const setupIdRef = useRef(null);
  const setupDoneRef = useRef(null);       // resolver of the "setup finished" wait
  const earlyProgressRef = useRef(new Map()); // progress that arrived before POST answered
  const refs = { teams: useRef(null), server: useRef(null), maps: useRef(null) };

  const set = (patch) => setForm(prev => ({ ...prev, ...patch }));
  const clearErr = (...keys) => setErrors(prev => { const n = { ...prev }; keys.forEach(k => delete n[k]); return n; });

  // ── data ──
  const loadServers = useCallback(() => axios.get('/api/servers/available').then(r => setServers(r.data || [])).catch(() => { }), []);
  useEffect(() => {
    let alive = true;
    Promise.allSettled([axios.get('/api/teams?limit=1000'), axios.get('/api/servers/available'), axios.get('/api/maps'), axios.get('/api/seasons')])
      .then(([tm, sv, mp, ss]) => {
        if (!alive) return;
        if (tm.status === 'fulfilled') setTeams(tm.value.data.data || tm.value.data || []);
        else setLoadError(tm.reason?.response?.data?.error || t('createMatch.loadFailed', 'Could not load teams.'));
        if (sv.status === 'fulfilled') setServers(sv.value.data || []);
        if (mp.status === 'fulfilled') setMapDict(mp.value.data || []);
        if (ss.status === 'fulfilled') setSeasons(ss.value.data || []);
        setLoaded(true);
      });
    const iv = setInterval(() => { if (!document.hidden) loadServers(); }, 10000);
    return () => { alive = false; clearInterval(iv); };
  }, [loadServers, t]);

  // ?season=<id> (season page → "Add match"): apply that preset once seasons are in
  const seasonParamDone = useRef(false);
  useEffect(() => {
    const want = params.get('season');
    if (seasonParamDone.current || !want || !seasons.length) return;
    seasonParamDone.current = true;
    const s = seasons.find(x => String(x.id) === String(want));
    if (s) loadSeason(s.id);
  }, [seasons]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── setup progress over the admin WebSocket ──
  useEffect(() => {
    let ws = null, retry = null, alive = true;
    const connect = () => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const host = window.location.hostname;
      const port = (host === 'localhost' || host === '127.0.0.1') ? ':5000' : (window.location.port ? `:${window.location.port}` : '');
      const token = localStorage.getItem('token');
      ws = new WebSocket(`${protocol}//${host}${port}/ws${token ? `?token=${token}` : ''}`);
      ws.onmessage = (ev) => {
        let msg; try { msg = JSON.parse(ev.data); } catch (_) { return; }
        if (msg.type !== 'match_setup_progress' || !msg.data) return;
        const d = msg.data;
        if (!setupIdRef.current) { earlyProgressRef.current.set(d.match_id, d); return; }
        if (d.match_id === setupIdRef.current) applyProgress(d);
      };
      ws.onclose = () => { if (alive) retry = setTimeout(connect, 3000); };
    };
    connect();
    return () => { alive = false; clearTimeout(retry); if (ws) ws.close(); };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const applyProgress = (d) => {
    setProgress(p => ({
      ...p, step: d.step, label: d.label || '',
      hasBackup: d.has_backup !== undefined ? !!d.has_backup : p.hasBackup,
      serverInfo: d.server_ip ? { ip: d.server_ip, port: d.server_port } : p.serverInfo,
    }));
    if ((d.step >= 8 || d.step === -1) && setupDoneRef.current) setupDoneRef.current();
  };

  // ── derived ──
  const mapOptions = useMemo(() => mapDict.filter(m => m.pool === mapMode), [mapDict, mapMode]);
  const poolSize = mapOptions.length;
  const vetoCheck = vetoPoolCheck(mapMode, poolSize);
  const team1 = teams.find(x => String(x.id) === String(form.team1_id)) || null;
  const team2 = teams.find(x => String(x.id) === String(form.team2_id)) || null;
  const server = servers.find(s => String(s.id) === String(form.server_id)) || null;
  // Custom avatars + the side backup file need a way to put files on the server: FTP/SFTP, or the LAN
  // agent (can_send_files; older panels only send has_backup). Off without one.
  // Enforce rosters kicks everyone not on a roster: off when a team has no players (quick match).
  const canSendFiles = (s) => !!s && !!(s.can_send_files ?? s.has_backup);
  const noFileAccess = !!server && !canSendFiles(server);
  const rosterCount = (x) => (Array.isArray(x?.players) ? x.players.length : (x?.player_count || 0));
  const emptyRoster = (!!team1 && rosterCount(team1) === 0) || (!!team2 && rosterCount(team2) === 0);
  useEffect(() => {
    // a season preset or an earlier pick may have switched them on
    if (noFileAccess && (form.custom_avatars || form.skip_backup_load)) setForm(prev => ({ ...prev, custom_avatars: false, skip_backup_load: false }));
    if (emptyRoster && form.enforce_teams) setForm(prev => ({ ...prev, enforce_teams: false }));
  }, [noFileAccess, emptyRoster, form.custom_avatars, form.skip_backup_load, form.enforce_teams]);
  const presets = ongoingSeasons(seasons);
  const season = seasons.find(s => String(s.id) === String(seasonId)) || null;
  const seatingOn = !!(season && season.stage_seating); // stage seating is a season option
  const team1Name = team1?.name || t('createMatch.team1', 'Team 1');
  const team2Name = team2?.name || t('createMatch.team2', 'Team 2');
  const bo = form.best_of;
  const mapsPicked = form.maps.slice(0, bo).filter(Boolean).length;
  const teamsDone = !!(team1 && team2 && team1.id !== team2.id);
  const mapsDone = form.veto_enabled ? !!form.veto_style : mapsPicked === bo;

  // ── actions ──
  function loadSeason(id) {
    const s = seasons.find(x => String(x.id) === String(id));
    if (!s) { setSeasonId(''); return; }
    setSeasonId(String(s.id));
    setMapMode(s.mode || 'active_duty');
    setForm(prev => applySeason(prev, s));
    setErrors({});
    setError('');
  }

  const changePool = (mode) => {
    if (mode === mapMode) return;
    setMapMode(mode);
    const size = mapDict.filter(m => m.pool === mode).length;
    const invalid = !vetoPoolCheck(mode, size).ok;
    setForm(prev => ({
      ...prev,
      maps: Array.from({ length: prev.best_of }, () => ''),
      map_sides: Array.from({ length: prev.best_of }, () => 'team1_ct'),
      ...(invalid ? { veto_enabled: false, veto_style: '' } : {}),
    }));
    setActiveSlot(0);
  };

  const setBestOf = (n) => { setForm(prev => withBestOf(prev, n)); if (activeSlot >= n) setActiveSlot(0); };

  // click a map tile: into the selected slot (or the first empty one); clicking a used map removes it
  const pickMap = (name) => {
    const maps = [...form.maps];
    const at = maps.slice(0, bo).indexOf(name);
    if (at >= 0) {
      maps[at] = '';
      setActiveSlot(at);
    } else {
      let slot = activeSlot < bo ? activeSlot : 0;
      if (maps[slot]) { const free = maps.slice(0, bo).findIndex(m => !m); if (free >= 0) slot = free; }
      maps[slot] = name;
      const next = maps.slice(0, bo).findIndex(m => !m);
      setActiveSlot(next >= 0 ? next : slot);
    }
    set({ maps });
    setErrors(prev => (prev.maps ? { ...prev, maps: undefined } : prev));
  };

  const clearSlot = (i) => setForm(prev => { const maps = [...prev.maps]; maps[i] = ''; return { ...prev, maps }; });

  const setSide = (i, val) => setForm(prev => {
    const map_sides = [...prev.map_sides]; map_sides[i] = val;
    const map_knife = [...(prev.map_knife || [])]; map_knife[i] = val === 'knife';
    return { ...prev, map_sides, map_knife, has_knife_round: map_sides.slice(0, prev.best_of).includes('knife') };
  });

  const swapTeams = () => setForm(prev => ({
    ...prev, team1_id: prev.team2_id, team2_id: prev.team1_id,
    // "Team A starts CT" stays true for Team A
    map_sides: prev.map_sides.map(s => (s === 'team1_ct' ? 'team2_ct' : s === 'team2_ct' ? 'team1_ct' : s)),
  }));

  const chooseServer = (s) => {
    if (s.status === 'busy') return;
    set({ server_id: s.id, ...(!canSendFiles(s) ? { skip_backup_load: false, custom_avatars: false } : {}) });
    clearErr('server_id');
  };

  const addDefaultMaps = async () => {
    setAddingDefaults(true);
    try {
      await axios.post('/api/maps/defaults', { pool: mapMode });
      const r = await axios.get('/api/maps');
      setMapDict(r.data || []);
    } catch (e) {
      setError(e.response?.data?.error || t('createMatch.defaultMapsFailed', 'Could not add the default maps.'));
    } finally { setAddingDefaults(false); }
  };

  const errorText = (code) => ({
    same_team: t('createMatch.errSameTeam', 'Team 1 and Team 2 cannot be the same'),
    teams: t('createMatch.errTeams', 'Please select both teams'),
    wingman_pool: t('createMatch.errWingmanPool', 'Wingman pool must have exactly 3, 5, or 7 maps for veto (currently has {{count}}). Go to Map Pool settings to fix this.', { count: poolSize }),
    veto_style: t('createMatch.errVetoStyle', 'Please select a veto style'),
    server: t('createMatch.errServer', 'Please select a server'),
    maps: t('createMatch.errMaps', 'Please select {{count}} map(s)', { count: bo }),
    seating: t('seating.createIncomplete', 'Pick 5 different players for each team.'),
  }[code] || code);

  const submit = async () => {
    if (creating) return;
    const v = validate(form, mapMode, poolSize);
    if (!v.message && seatingOn && seating && !seatingComplete(seating)) { v.errors = { ...v.errors, team1_id: true }; v.message = 'seating'; }
    setErrors(v.errors);
    if (v.message) {
      setError(errorText(v.message));
      const first = (v.errors.team1_id || v.errors.team2_id) ? refs.teams : v.errors.server_id ? refs.server : refs.maps;
      if (first.current) first.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }
    setError('');
    setCreating(true);
    setProgress({ step: 0, label: form.veto_enabled ? 'Creating match with veto...' : 'Submitting match...', hasBackup: true, serverInfo: null });
    setupIdRef.current = null;
    earlyProgressRef.current = new Map();
    try {
      const res = await axios.post('/api/matches', { ...buildPayload(form, { mapMode, poolSize, seasonId }), ...(seatingOn && seating ? { seating } : {}) });
      if (res.data.veto_enabled && res.data.veto_links) {
        setVetoResult({ links: res.data.veto_links, matchId: res.data.id || res.data.match_id || null });
        setCreating(false);
        return;
      }
      const { id, sent_to_server, server_ip, server_port, has_backup } = res.data;
      setupIdRef.current = id;
      setProgress(p => ({ ...p, hasBackup: has_backup !== undefined ? !!has_backup : p.hasBackup, serverInfo: server_ip ? { ip: server_ip, port: server_port } : p.serverInfo }));
      if (sent_to_server) {
        await new Promise(resolve => {
          const timer = setTimeout(resolve, has_backup ? 60000 : 20000);
          setupDoneRef.current = () => { clearTimeout(timer); setTimeout(resolve, 800); };
          const early = earlyProgressRef.current.get(id);
          if (early) applyProgress(early);
        });
      } else {
        await new Promise(r => setTimeout(r, 1500));
      }
      navigate(`/admin/matches/${id}`);
    } catch (e) {
      setError(e.response?.data?.error || t('createMatch.createFailed', 'Failed to create match'));
      setCreating(false);
    } finally {
      setupIdRef.current = null;
      setupDoneRef.current = null;
    }
  };

  // Ctrl/Cmd + Enter creates the match
  const submitRef = useRef(submit);
  submitRef.current = submit;
  useEffect(() => {
    const onKey = (e) => { if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') { e.preventDefault(); submitRef.current(); } };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  // ── options ──
  const teamOption = (x) => ({ value: x.id, label: x.name, team: x });
  const teamOptions = teams.map(teamOption);
  const filterTeam = (opt, input) => {
    const q = input.trim().toLowerCase();
    return !q || opt.data.team.name.toLowerCase().includes(q) || String(opt.data.team.tag || '').toLowerCase().includes(q);
  };
  const playersOf = (x) => (Array.isArray(x?.players) ? x.players.length : (x?.player_count || 0));
  // menu: logo, name, tag, player count; the chosen value: logo + name only (the card below has the rest)
  const teamLabel = (opt, { context }) => (
    <span className="cmp-opt">
      <TeamLogo team={opt.team} size={22} radius={6} />
      <span className="name">{opt.team.name}</span>
      {context === 'menu' && opt.team.tag && <span className="tag">{opt.team.tag}</span>}
      {context === 'menu' && (
        <span className="count">
          {t('createMatch.playersCount', '{{count}} players', { count: playersOf(opt.team) })}
        </span>
      )}
    </span>
  );

  // Super admins get every server of the panel from the API: show their own by default (same
  // ownership rule as the Matches page), the other admins' servers behind a switch. Panel admins
  // already only receive their own. Then search, then the first SERVER_LIMIT unless "show more".
  const SERVER_LIMIT = 6;
  const isSuperAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const isMine = (s) => (user?.steam_id && s.created_by_steam === user.steam_id)
    || (!s.created_by_steam && s.created_by != null && String(s.created_by) === String(user?.id));
  const ownServers = isSuperAdmin ? servers.filter(isMine) : servers;
  const otherCount = servers.length - ownServers.length;
  // nothing of his own → show everything (no empty list behind a switch)
  const ownerScoped = isSuperAdmin && !showOtherServers && ownServers.length > 0 ? ownServers : servers;
  const q = serverQuery.trim().toLowerCase();
  const matchingServers = ownerScoped.filter(s => !q || `${s.name} ${s.ip} ${s.public_ip || ''} ${s.port}`.toLowerCase().includes(q));
  let shownServers = (showAllServers || q) ? matchingServers : matchingServers.slice(0, SERVER_LIMIT);
  const chosen = servers.find(s => String(s.id) === String(form.server_id));
  if (chosen && !shownServers.includes(chosen)) shownServers = [...shownServers, chosen]; // the selected one never disappears
  const hiddenCount = matchingServers.length - shownServers.filter(s => matchingServers.includes(s)).length;
  const serverAddr = (s) => `${s.lan_server_id ? (s.public_ip || (String(s.ip).startsWith('lan.') ? '-' : s.ip)) : s.ip}:${s.port}`;
  const statusText = (s) => t(`serverStatus.${s.status}`, s.status === 'available' ? 'Available' : s.status === 'busy' ? 'Busy' : 'Offline');

  const sideOptions = (i) => [
    { value: 'team1_ct', label: t('createMatch.sideCt', '{{team}} CT', { team: team1?.tag || team1Name }) },
    { value: 'team2_ct', label: t('createMatch.sideCt', '{{team}} CT', { team: team2?.tag || team2Name }) },
    { value: 'knife', label: t('createMatch.knife', 'Knife') },
  ];
  const sideText = (val) => (val === 'knife' ? t('createMatch.knifeRound', 'Knife round') : t('createMatch.startsCt', '{{team}} starts CT', { team: val === 'team2_ct' ? team2Name : team1Name }));
  const mapImg = (name) => (mapDict.find(m => m.name === name && m.pool === mapMode) || {}).image_url || '';

  const RULES = [
    { key: 'admin_only_start', label: t('createMatch.adminOnlyStart', 'Admin Only Start'), hint: t('createMatch.adminOnlyStartHint', "Players can't use .ready") },
    { key: 'enforce_teams', label: t('createMatch.enforceRosters', 'Enforce Rosters'), hint: emptyRoster ? t('createMatch.enforceRostersRequires', 'Needs players on both teams') : t('createMatch.enforceRostersHint', 'Kick players not on roster'), disabled: emptyRoster },
    { key: 'allow_stop', label: t('createMatch.allowStop', 'Allow !stop Command'), hint: t('createMatch.allowStopHint', 'Players can request a stop') },
    { key: 'skip_backup_load', label: t('createMatch.skipBackup', 'Skip Backup Load'), hint: noFileAccess ? t('createMatch.skipBackupRequires', 'Requires FTP/SFTP on selected server') : t('createMatch.skipBackupHint', 'Skip auto team backup on match start'), disabled: noFileAccess },
    { key: 'custom_avatars', label: t('createMatch.customAvatars', 'Custom Avatars'), hint: noFileAccess ? t('createMatch.skipBackupRequires', 'Requires FTP/SFTP on selected server') : t('createMatch.customAvatarsHint', 'Deploy player avatars to server'), disabled: noFileAccess },
    { key: 'random_password', label: t('createMatch.randomPassword', 'Random Password Protection'), hint: t('createMatch.randomPasswordHint', 'Sets a random EU city as the server password') },
  ];
  const BROADCAST = [
    { key: 'show_killfeed', label: t('createSeason.showKillfeed', 'Show Killfeed'), hint: t('createSeason.showKillfeedHint', 'Public killfeed visible on match pages') },
    { key: 'cstv1_public', label: t('createSeason.cstv1Public', 'CSTV 1 Public'), hint: t('createSeason.cstv1PublicHint', 'Show CSTV 1 connect info on the public match page') },
    { key: 'cstv2_public', label: t('createSeason.cstv2Public', 'CSTV 2 Public'), hint: t('createSeason.cstv2PublicHint', 'Show CSTV 2 connect info on the public match page') },
  ];

  const cvarList = (key, title) => (
    <div>
      <div className="cmp-field-label">{title}</div>
      {(form[key].length ? form[key] : ['']).map((cmd, i, arr) => (
        <div key={i} className="cmp-cvar-row">
          <input className="cmp-input" value={cmd} maxLength={256} placeholder={t('createMatch.commandN', 'command {{n}}', { n: i + 1 })}
            onChange={e => { const a = [...(form[key].length ? form[key] : [''])]; a[i] = e.target.value; set({ [key]: a }); }}
            onKeyDown={e => {
              if (e.key === 'Tab' && !e.shiftKey && i === arr.length - 1 && arr.length < 20 && cmd.trim()) { e.preventDefault(); set({ [key]: [...arr, ''] }); }
            }} />
          {arr.length > 1 && <button type="button" className="cmp-icon-btn" title={t('common.remove', 'Remove')} onClick={() => set({ [key]: arr.filter((_, j) => j !== i) })}>×</button>}
        </div>
      ))}
      {form[key].length < 20 && <button type="button" className="cmp-link-btn" onClick={() => set({ [key]: [...(form[key].length ? form[key] : ['']), ''] })}>+ {t('createMatch.addCommand', 'Add command')}</button>}
    </div>
  );

  if (!loaded) return <div className="cmp"><div className="cmp-loading">{t('common.loading', 'Loading...')}</div></div>;

  const vetoReason = !vetoCheck.ok && (vetoCheck.reason === 'active_duty'
    ? t('createMatch.vetoNeedsAd', 'Active Duty needs exactly 7 maps (currently {{count}})', { count: poolSize })
    : vetoCheck.reason === 'wingman'
      ? t('createMatch.vetoNeedsWingman', 'Wingman needs 3, 5, or 7 maps (currently {{count}})', { count: poolSize })
      : t('createMatch.vetoNoCompetitive', 'Veto is not available for the Competitive pool'));
  const poolLabel = (POOLS.find(p => p.value === mapMode) || {}).label;
  const rulesOn = RULES.filter(r => form[r.key] && !r.disabled);

  // season options that apply while the match runs (stored on the season, not on this form)
  const listOf = (v) => {
    if (!v) return [];
    if (Array.isArray(v)) return v;
    try { const a = JSON.parse(v); if (Array.isArray(a)) return a; } catch (_) { }
    return String(v).split(/[\n;]/).map(s => s.trim()).filter(Boolean);
  };
  const tempAdmins = (season && season.temp_admins) || [];
  const seasonExtras = season ? [
    { key: 'halftime', name: t('createMatch.exHalftime', 'Halftime break'), on: !!season.halftime_break_enabled,
      detail: t('createMatch.exHalftimeOn', '{{min}} min, coach talk {{talk}}', { min: season.halftime_break_duration || 5, talk: season.halftime_coach_talk ? t('createMatch.on', 'On') : t('createMatch.off', 'Off') }) },
    { key: 'ts', name: 'TeamSpeak', on: !!season.ts_enabled,
      detail: t('createMatch.exTs', 'Coach mute/unmute, {{host}}', { host: season.ts_host || '-' }) },
    { key: 'akros', name: t('createMatch.exAkros', 'Akros anti-cheat'), on: !!season.akros_enabled,
      detail: t('createMatch.exAkrosOn', 'Anti-cheat checks for every player') },
    { key: 'hltv', name: 'HLTV', on: !!season.hltv_enabled,
      detail: t('createMatch.exHltv', '{{count}} broadcast commands', { count: listOf(season.hltv_commands).length }) },
    { key: 'temp', name: t('createMatch.exTempAdmins', 'Temp admins'), on: tempAdmins.length > 0,
      detail: tempAdmins.map(a => a.name || a.steam_id).join(', ') },
    { key: 'prefix', name: t('createMatch.exChatPrefix', 'Chat prefix'), on: !!season.chat_prefix,
      detail: season.chat_prefix || '' },
    { key: 'seating', name: t('createSeason.stageSeating', 'Stage seating'), on: !!season.stage_seating,
      detail: t('createMatch.exSeatingOn', 'Seating step in section 1, editable on the match page') },
  ] : [];

  return (
    <div className="cmp">
      {/* header */}
      <div className="cmp-head">
        <div>
          <Link to="/admin/matches" className="cmp-back">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M15 18l-6-6 6-6" /></svg>
            {t('nav.matches', 'Matches')}
          </Link>
          <h1 className="cmp-title">{t('createMatch.title', 'Create New Match')}</h1>
          <p className="cmp-sub">{t('createMatch.subtitle', 'Configure teams, server, and map pool')}</p>
        </div>
        {presets.length > 0 && (
          <div className="cmp-preset">
            <span className="cmp-preset-label">{t('createMatch.seasonPreset', 'Season preset')}</span>
            <div className="cmp-preset-sel">
              <AnimSelect value={seasonId} label={season ? season.name : ''} placeholder={t('createMatch.loadPreset', 'Load preset...')}
                options={[{ value: '', label: t('createMatch.noPreset', 'No preset') }, ...presets.map(s => ({ value: String(s.id), label: s.name }))]}
                onChange={val => (val ? loadSeason(val) : setSeasonId(''))} />
            </div>
            {season && <span className="cmp-chip green">✓ {t('createMatch.loaded', 'Loaded')}</span>}
          </div>
        )}
      </div>
      {loadError && <div className="cmp-error gap">{loadError}</div>}

      <div className="cmp-grid">
        <div className="cmp-main">
          {/* 1 matchup */}
          <Section no={1} done={teamsDone} innerRef={refs.teams} error={errors.team1_id || errors.team2_id}
            title={t('createMatch.secMatchup', 'Matchup')} hint={t('createMatch.secMatchupHint', 'Pick the two teams. Teams without players work too - players are placed by the side they join.')}>
            <div className="cmp-matchup">
              {[['team1_id', team1, form.team2_id, t('createMatch.team1', 'Team 1')], ['team2_id', team2, form.team1_id, t('createMatch.team2', 'Team 2')]].map(([key, tm, other, label], idx) => (
                <React.Fragment key={key}>
                  {idx === 1 && (
                    <div className="cmp-vs">
                      <span className="cmp-vs-badge">VS</span>
                      <button type="button" className="cmp-icon-btn" title={t('createMatch.swapTeams', 'Swap teams')} onClick={swapTeams} disabled={!form.team1_id && !form.team2_id}>⇄</button>
                    </div>
                  )}
                  <div className="cmp-team">
                    <span className="cmp-team-label">{label}<i>*</i></span>
                    <Select options={teamOptions} value={tm ? teamOption(tm) : null} isClearable
                      placeholder={t('createMatch.searchTeam', 'Search team...')}
                      formatOptionLabel={teamLabel} filterOption={filterTeam}
                      isOptionDisabled={o => String(o.value) === String(other)}
                      noOptionsMessage={() => t('createMatch.noTeams', 'No teams - create them on the Teams page')}
                      styles={selectStyles(errors[key])}
                      onChange={sel => { set({ [key]: sel ? sel.value : '' }); clearErr('team1_id', 'team2_id'); setError(''); }} />
                    <div className={`cmp-team-card${tm ? ' set' : ''}`}>
                      {tm ? (<>
                        <TeamLogo team={tm} size={38} radius={9} />
                        <div className="cmp-team-info">
                          <div className="cmp-team-name">{tm.name}</div>
                          <div className="cmp-team-meta">
                            {tm.tag && <span>{tm.tag}</span>}
                            <span>{playersOf(tm)
                              ? t('createMatch.playersCount', '{{count}} players', { count: playersOf(tm) })
                              : t('createMatch.noRoster', 'No roster - quick match')}</span>
                            {tm.coach_name && <span>{t('createMatch.coach', 'Coach')}: {tm.coach_name}</span>}
                          </div>
                        </div>
                      </>) : <span className="cmp-team-empty">{t('createMatch.noTeamYet', 'No team selected')}</span>}
                    </div>
                  </div>
                </React.Fragment>
              ))}
            </div>
            {seatingOn && teamsDone && playersOf(team1) > 0 && playersOf(team2) > 0 && (
              <SeatingStep team1Id={team1.id} team2Id={team2.id} value={seating} onChange={setSeating} />
            )}
          </Section>

          {/* 2 server & format */}
          <Section no={2} done={!!server} innerRef={refs.server} error={errors.server_id}
            title={t('createMatch.secServer', 'Server & format')} hint={t('createMatch.secServerHint', 'Busy servers are hosting another match.')}
            extra={<div className="cmp-seg big">{[1, 3, 5].map(n => <button key={n} type="button" className={bo === n ? 'on' : ''} onClick={() => setBestOf(n)}>BO{n}</button>)}</div>}>
            {(ownerScoped.length > SERVER_LIMIT || (isSuperAdmin && otherCount > 0)) && (
              <div className="cmp-srv-tools">
                {ownerScoped.length > SERVER_LIMIT && (
                  <input className="cmp-search" placeholder={t('createMatch.searchServer', 'Search servers...')} value={serverQuery} onChange={e => setServerQuery(e.target.value)} />
                )}
                {isSuperAdmin && otherCount > 0 && ownServers.length > 0 && (
                  <button type="button" role="switch" aria-checked={showOtherServers} className={`cmp-owner-sw${showOtherServers ? ' on' : ''}`}
                    onClick={() => { const on = !showOtherServers; setShowOtherServers(on); setShowAllServers(on); /* on: show them right away, not behind "show more" */ }}>
                    <span className={`cmp-switch${showOtherServers ? ' on' : ''}`} aria-hidden="true" />
                    {t('createMatch.showOthersServers', "Other admins' servers ({{count}})", { count: otherCount })}
                  </button>
                )}
                {isSuperAdmin && otherCount > 0 && ownServers.length === 0 && (
                  <span className="cmp-pool-hint">{t('createMatch.noOwnServers', 'You have no servers of your own - showing every server.')}</span>
                )}
              </div>
            )}
            {servers.length === 0 ? (
              <div className="cmp-empty">{t('createMatch.noServers', 'No free servers.')} <Link to="/admin/servers">{t('createMatch.openServers', 'Open Servers')}</Link></div>
            ) : (
              <div className="cmp-servers">
                {shownServers.map(s => (
                  <button key={s.id} type="button" className={`cmp-srv${String(s.id) === String(form.server_id) ? ' on' : ''}`} disabled={s.status === 'busy'} onClick={() => chooseServer(s)}>
                    <span className="cmp-srv-main">
                      <span className="cmp-srv-name">{s.name}</span>
                      <span className="cmp-srv-addr">{serverAddr(s)}</span>
                    </span>
                    <span className="cmp-srv-tags">
                      <span className="line">
                        <span className={`cmp-status ${STATUS_KEYS.includes(s.status) ? s.status : 'offline'}`}>{statusText(s)}</span>
                        {s.status === 'available' && s.max_players ? <span className="players">{s.player_count ?? 0}/{s.max_players}</span> : null}
                      </span>
                      <span className="line">
                        {s.lan_server_id && <span className="cmp-chip sub">{t('lan.badge', 'LAN')}</span>}
                        {s.has_backup ? <span className="cmp-chip blue" title={t('createMatch.sftpHint', 'SFTP/FTP set: side files, round backups')}>SFTP</span> : null}
                      </span>
                    </span>
                  </button>
                ))}
                {shownServers.length === 0 && <div className="cmp-empty wide">{t('createMatch.noServerMatch', 'No server matches your search.')}</div>}
              </div>
            )}
            {!q && (hiddenCount > 0 || showAllServers) && matchingServers.length > SERVER_LIMIT && (
              <button type="button" className="cmp-more" onClick={() => setShowAllServers(v => !v)}>
                {showAllServers ? t('createMatch.showLess', 'Show less') : t('createMatch.showMoreServers', 'Show {{count}} more', { count: hiddenCount })}
                <span className={`cmp-caret${showAllServers ? ' open' : ''}`} aria-hidden="true">▸</span>
              </button>
            )}
          </Section>

          {/* 3 maps / veto */}
          <Section no={3} done={mapsDone} innerRef={refs.maps} error={errors.maps || errors.veto_style || errors.wingman_pool}
            title={form.veto_enabled ? t('createMatch.secVeto', 'Map veto') : t('createMatch.secMaps', 'Maps & sides')}
            hint={form.veto_enabled ? t('createMatch.secVetoHint', 'Teams ban and pick maps from the veto links after the match is created.') : t('createMatch.secMapsHint', 'Click a map to put it in the selected slot. Click it again to remove it.')}
            extra={<div className="cmp-seg">{POOLS.map(p => <button key={p.value} type="button" className={mapMode === p.value ? 'on' : ''} onClick={() => changePool(p.value)}>{t(`mapsPage.pool_${p.value}`, p.label)}</button>)}</div>}>
            {(poolSize === 0 || (mapMode === 'active_duty' && poolSize < 7)) && (
              <div className="cmp-warn">
                <span>{poolSize === 0
                  ? t('createMatch.poolEmpty', 'You have no maps in your {{pool}} pool yet - a match needs at least one map.', { pool: poolLabel })
                  : t('createMatch.poolShort', 'Your Active Duty pool has {{count}}/7 maps - veto needs exactly 7.', { count: poolSize })}</span>
                <Link to={`/admin/maps?pool=${mapMode}`}>{t('createMatch.openMapsPage', 'Open Maps page')}</Link>
                <button type="button" className="cmp-btn" onClick={addDefaultMaps} disabled={addingDefaults}>
                  {addingDefaults ? t('createMatch.addingDefaults', 'Adding…') : t('createMatch.addDefaultMaps', 'Add default maps')}
                </button>
              </div>
            )}

            <div className={`cmp-veto-card${form.veto_enabled ? ' on' : ''}${!vetoCheck.ok ? ' off' : ''}`}>
              <div>
                <div className="cmp-toggle-label">{t('createMatch.enableVeto', 'Enable Veto')}</div>
                <div className={`cmp-toggle-hint${vetoReason ? ' warn' : ''}`}>{vetoReason || t('createMatch.enableVetoHint', 'Teams ban/pick maps via veto links')}</div>
              </div>
              <button type="button" role="switch" aria-checked={form.veto_enabled} aria-label={t('createMatch.enableVeto', 'Enable Veto')} disabled={!vetoCheck.ok} className="cmp-switch-btn"
                onClick={() => { const on = !form.veto_enabled; set({ veto_enabled: on, veto_style: on ? 'vrs' : '', veto_use_coin_flip: on ? form.veto_use_coin_flip : false }); clearErr('maps', 'veto_style'); }}>
                <Switch on={form.veto_enabled} />
              </button>
            </div>

            {form.veto_enabled ? (<>
              <div className="cmp-row gap">
                <div className="cmp-field-label flush">{t('createMatch.vetoStyle', 'Veto Style')}</div>
                <button type="button" className="cmp-link-btn" onClick={() => setAlert(t('createMatch.vetoStyleHelp',
                  "VRS Type - Teams alternate banning maps. In BO1, the last remaining map is played. In BO3/BO5, teams also pick maps and choose sides for the opponent's pick.\n\nFaceit Style - Similar to FACEIT matchmaking. Teams alternate bans, then picks with side selection. The final decider map is always decided by knife round (no side pick).\n\nThe Best Of selector (BO1 / BO3 / BO5) determines how many maps are played. The veto step sequence adjusts automatically."))}>
                  {t('createMatch.howVetoWorks', 'How do they work?')}
                </button>
              </div>
              <div className="cmp-veto-opts">
                {[{ value: 'vrs', label: 'VRS', desc: t('createMatch.vrsDesc', 'Ban → Pick → Side') }, { value: 'faceit', label: 'FACEIT', desc: t('createMatch.faceitDesc', 'Ban → Pick → Knife') }].map(s => (
                  <button key={s.value} type="button" className={`cmp-style${form.veto_style === s.value ? ' on' : ''}${errors.veto_style ? ' err' : ''}`}
                    onClick={() => { set({ veto_style: s.value }); clearErr('veto_style'); }}>
                    <b>{s.label}</b><span>{s.desc}</span>
                    {form.veto_style === s.value && <em>{vetoFormat({ ...form, veto_style: s.value }, mapMode, poolSize)}</em>}
                  </button>
                ))}
              </div>
              <div className="cmp-toggles gap">
                <Toggle on={form.veto_use_coin_flip} label={t('createMatch.coinFlip', 'Coin Flip')} hint={t('createMatch.coinFlipHint', 'Random coin flip decides who starts the veto')} onChange={v => set({ veto_use_coin_flip: v })} />
                <Toggle on={form.steam_login_required} label={t('createMatch.steamLoginRequired', 'Steam Login Required for Veto')} hint={t('createMatch.steamLoginRequiredHint', 'Players must login with Steam to participate in veto')} onChange={v => set({ steam_login_required: v })} />
              </div>
              <div className="cmp-field-label">{t('createMatch.vetoDiscordWebhook', 'Discord Channel Webhook')} <span className="opt">({t('common.optional', 'optional')})</span></div>
              <input className="cmp-input" type="url" placeholder="https://discord.com/api/webhooks/..." value={form.veto_discord_webhook} onChange={e => set({ veto_discord_webhook: e.target.value })} />
              <div className="cmp-note">{t('createMatch.vetoDiscordWebhookHint', 'When set, the veto links (with team names) are posted to this Discord channel on match creation. The admin control link is never posted.')}</div>
            </>) : (<>
              <div className="cmp-slots">
                {Array.from({ length: bo }).map((_, i) => {
                  const m = form.maps[i];
                  const img = m && mapImg(m);
                  return (
                    <div key={i} className={`cmp-slot${activeSlot === i ? ' active' : ''}${errors.maps?.includes(i) ? ' err' : ''}`} onClick={() => setActiveSlot(i)}>
                      <span className="cmp-slot-no">M{i + 1}</span>
                      <div className={`cmp-slot-thumb${m ? '' : ' empty'}`} style={img ? { backgroundImage: `url("${img}")` } : undefined}>
                        <span>{m ? shortMap(m) : (activeSlot === i ? t('createMatch.pickBelow', 'Pick a map below') : t('createMatch.emptySlot', 'Empty'))}</span>
                      </div>
                      <div className="cmp-slot-sides" onClick={e => e.stopPropagation()}>
                        <div className="cmp-seg">
                          {sideOptions(i).map(o => (
                            <button key={o.value} type="button" title={o.value === 'knife' ? sideText('knife') : sideText(o.value)}
                              className={(form.map_sides[i] || 'team1_ct') === o.value ? 'on' : ''} onClick={() => setSide(i, o.value)}>{o.label}</button>
                          ))}
                        </div>
                      </div>
                      {m ? <button type="button" className="cmp-icon-btn" title={t('common.remove', 'Remove')} onClick={e => { e.stopPropagation(); clearSlot(i); setActiveSlot(i); }}>×</button> : <span className="cmp-icon-gap" />}
                    </div>
                  );
                })}
              </div>
              <div className="cmp-pool-head">
                <div className="cmp-field-label flush">{t('createMatch.mapPoolN', '{{pool}} pool, {{count}} maps', { pool: t(`mapsPage.pool_${mapMode}`, poolLabel), count: poolSize })}</div>
                <span className="cmp-pool-hint">{t('createMatch.fillingSlot', 'Filling M{{n}}', { n: Math.min(activeSlot, bo - 1) + 1 })}</span>
              </div>
              <div className="cmp-tiles">
                {mapOptions.map(m => {
                  const slot = form.maps.slice(0, bo).indexOf(m.name);
                  return (
                    <button key={m.id || m.name} type="button" className={`cmp-tile${slot >= 0 ? ' used' : ''}${m.image_url ? '' : ' noimg'}`}
                      style={m.image_url ? { backgroundImage: `url("${m.image_url}")` } : undefined} onClick={() => pickMap(m.name)} title={m.name}>
                      {m.workshop_id && <span className="cmp-tile-ws">WS</span>}
                      {slot >= 0 && <span className="cmp-tile-badge">M{slot + 1}</span>}
                      <span className="cmp-tile-name">{shortMap(m.name)}</span>
                    </button>
                  );
                })}
              </div>
            </>)}
          </Section>

          {/* 4 rules */}
          <Section no={4} title={t('createMatch.secRules', 'Match rules')} hint={season ? t('createMatch.fromSeason', 'Loaded from the season preset - change them for this match only.') : null}>
            <div className="cmp-toggles">
              {RULES.map(r => <Toggle key={r.key} on={!!form[r.key] && !r.disabled} label={r.label} hint={r.hint} disabled={r.disabled} onChange={v => set({ [r.key]: v })} />)}
              <div className="cmp-toggle static">
                <span>
                  <span className="cmp-toggle-label">{t('createMatch.techPauseBudget', 'Tech Pause Time Budget')}</span>
                  <span className="cmp-toggle-hint">{t('createMatch.techBudgetHint', 'Max seconds per team can use for technical pauses (0 = unlimited)')}</span>
                </span>
                <span className="cmp-num">
                  <input type="number" min="0" max="7200" value={form.tech_budget_seconds}
                    onChange={e => set({ tech_budget_seconds: Math.min(7200, Math.max(0, parseInt(e.target.value, 10) || 0)) })} />
                  <span>{form.tech_budget_seconds > 0 ? `${Math.floor(form.tech_budget_seconds / 60)}:${String(form.tech_budget_seconds % 60).padStart(2, '0')}` : t('createMatch.unlimited', 'unlimited')}</span>
                </span>
              </div>
            </div>
          </Section>

          {/* 5 broadcast */}
          <Section no={5} title={t('createSeason.broadcastPublic', 'Broadcast & Public')}>
            <div className="cmp-toggles">
              {BROADCAST.map(r => <Toggle key={r.key} on={!!form[r.key]} label={r.label} hint={r.hint} onChange={v => set({ [r.key]: v })} />)}
              <div className="cmp-toggle static">
                <span>
                  <span className="cmp-toggle-label">{t('createSeason.streamDelay', 'Stream Broadcast Delay')}</span>
                  <span className="cmp-toggle-hint">{t('createSeason.streamDelayHint', 'Delay public score & killfeed by N seconds (0 = off)')}</span>
                </span>
                <span className="cmp-num">
                  <input type="number" min="0" max="300" value={form.stream_delay}
                    onChange={e => set({ stream_delay: Math.min(300, Math.max(0, parseInt(e.target.value, 10) || 0)) })} />
                  <span>{t('createSeason.seconds', 'sec')}</span>
                </span>
              </div>
            </div>
          </Section>

          {/* 6 season extras: what the season preset applies while the match runs (read-only) */}
          <Section no={6} done={!!season} title={t('createMatch.secExtras', 'Season extras')}
            hint={season
              ? t('createMatch.secExtrasHint', 'From {{season}} - applied while the match runs. Change them in the season settings.', { season: season.name })
              : t('createMatch.secExtrasNoSeason', 'HLTV, Akros anti-cheat, TeamSpeak, halftime break and temp admins come from a season.')}
            extra={season && <Link className="cmp-btn" to={`/admin/seasons/${season.id}/settings`}>{t('createMatch.editSeason', 'Edit season')}</Link>}>
            {season ? (
              <div className="cmp-extras">
                {seasonExtras.map(x => (
                  <div key={x.key} className="cmp-extra">
                    <div className="cmp-extra-top">
                      <span className="cmp-extra-name">{x.name}</span>
                      <span className={`cmp-chip ${x.on ? 'green' : 'muted'}`}>{x.on ? t('createMatch.on', 'On') : t('createMatch.off', 'Off')}</span>
                    </div>
                    <div className="cmp-extra-detail" title={x.detail || ''}>{x.on ? x.detail : t('createMatch.exOff', 'Off in this season')}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="cmp-extras-empty">
                <span>{t('createMatch.extrasHowTo', 'Load a season preset at the top to use them for this match, or create a season with these settings.')}</span>
                <Link className="cmp-btn" to="/admin/seasons">{t('createMatch.openSeasons', 'Open Seasons')}</Link>
              </div>
            )}
          </Section>

          {/* 7 advanced */}
          <section className="cmp-sec">
            <button type="button" className="cmp-sec-head cmp-sec-toggle" onClick={() => setAdvancedOpen(o => !o)} aria-expanded={advancedOpen}>
              <span className="cmp-sec-no">7</span>
              <div>
                <div className="cmp-sec-title">{t('createMatch.secAdvanced', 'Custom commands (CVARs)')}</div>
                <div className="cmp-sec-hint">{t('createMatch.secAdvancedHint', 'Extra server commands for warmup and for knife / live.')}</div>
              </div>
              {(form.cvars_warmup.filter(c => c.trim()).length + form.cvars_knife_live.filter(c => c.trim()).length) > 0 && (
                <span className="cmp-chip blue">{form.cvars_warmup.filter(c => c.trim()).length + form.cvars_knife_live.filter(c => c.trim()).length}</span>
              )}
              <span className={`cmp-caret${advancedOpen ? ' open' : ''}`} aria-hidden="true">▸</span>
            </button>
            {advancedOpen && (
              <div className="cmp-sec-body">
                <div className="cmp-cvars">
                  {cvarList('cvars_warmup', t('createMatch.warmupCommands', 'Warmup Commands'))}
                  {cvarList('cvars_knife_live', t('createMatch.knifeLiveCommands', 'Knife / Live Commands'))}
                </div>
                <div className="cmp-note">{t('createMatch.cvarsHint', 'Tab on the last field adds another. Max 20 each. A season preset fills these.')}</div>
              </div>
            )}
          </section>
        </div>

        {/* preview */}
        <aside className="cmp-aside">
          <div className="cmp-prev">
            <div className="cmp-prev-head">
              <span>{t('createMatch.preview', 'Match preview')}</span>
              {season && <span className="cmp-chip green">{season.name}</span>}
            </div>
            <div className="cmp-prev-vs">
              {[team1, team2].map((tm, i) => (
                <React.Fragment key={i}>
                  {i === 1 && <div className="cmp-prev-mid"><strong>VS</strong><span className="cmp-chip blue">BO{bo}</span></div>}
                  <div className={`cmp-prev-team${tm ? '' : ' empty'}`}>
                    {tm ? <TeamLogo team={tm} size={52} radius={12} /> : <span className="cmp-prev-ph">?</span>}
                    <b>{tm ? tm.name : (i === 0 ? t('createMatch.team1', 'Team 1') : t('createMatch.team2', 'Team 2'))}</b>
                  </div>
                </React.Fragment>
              ))}
            </div>

            <div className="cmp-prev-block">
              <div className="cmp-prev-label">{t('createMatch.server', 'Server')}</div>
              {server ? (
                <div className="cmp-prev-line">
                  <span className="grow">{server.name} <span className="addr">{serverAddr(server)}</span></span>
                  <span className={`cmp-status ${STATUS_KEYS.includes(server.status) ? server.status : 'offline'}`}>{statusText(server)}</span>
                </div>
              ) : <div className="cmp-prev-line"><span className="muted">{t('createMatch.noServerYet', 'No server selected')}</span></div>}
            </div>

            <div className="cmp-prev-block">
              <div className="cmp-prev-label"><span>{t('createMatch.maps', 'Maps')}</span><span>{t(`mapsPage.pool_${mapMode}`, poolLabel)}</span></div>
              {form.veto_enabled ? (
                <div className="cmp-prev-line">
                  <span className="cmp-chip sub">{t('createMatch.veto', 'Veto')}</span>
                  <span className="grow">
                    {form.veto_style
                      ? <><span>{form.veto_style === 'faceit' ? 'FACEIT' : 'VRS'}</span><span>{vetoFormat(form, mapMode, poolSize)}</span></>
                      : <span>{t('createMatch.pickStyle', 'Pick a veto style')}</span>}
                    {form.veto_use_coin_flip && <span>{t('createMatch.coinFlip', 'Coin Flip')}</span>}
                  </span>
                </div>
              ) : Array.from({ length: bo }).map((_, i) => (
                <div key={i} className="cmp-prev-map">
                  <i>M{i + 1}</i>
                  <span className={form.maps[i] ? '' : 'empty'}>{form.maps[i] ? shortMap(form.maps[i]) : t('createMatch.notPicked', 'not picked')}</span>
                  {form.maps[i] && <small>{sideText(form.map_sides[i] || 'team1_ct')}</small>}
                </div>
              ))}
            </div>

            <div className="cmp-prev-block">
              <div className="cmp-prev-label">{t('createMatch.secRules', 'Match rules')}</div>
              <div className="cmp-chips">
                {rulesOn.length ? rulesOn.map(r => <span key={r.key} className="cmp-chip muted">{r.label}</span>) : <span className="cmp-chip muted">{t('createMatch.defaultRules', 'Defaults')}</span>}
                {form.tech_budget_seconds > 0 && <span className="cmp-chip muted">{t('createMatch.techChip', 'Tech {{time}}', { time: `${Math.floor(form.tech_budget_seconds / 60)}:${String(form.tech_budget_seconds % 60).padStart(2, '0')}` })}</span>}
                {form.stream_delay > 0 && <span className="cmp-chip amber">{t('createMatch.delayChip', 'Delay {{s}}s', { s: form.stream_delay })}</span>}
                {!form.show_killfeed && <span className="cmp-chip muted">{t('createMatch.noKillfeed', 'No public killfeed')}</span>}
                {seasonExtras.filter(x => x.on).map(x => <span key={x.key} className="cmp-chip green">{x.name}</span>)}
              </div>
            </div>

            <div className="cmp-prev-block">
              <ul className="cmp-todo">
                <li className={teamsDone ? 'ok' : (errors.team1_id || errors.team2_id) ? 'bad' : ''}>{t('createMatch.todoTeams', 'Two different teams')}</li>
                <li className={server ? 'ok' : errors.server_id ? 'bad' : ''}>{t('createMatch.todoServer', 'A server')}</li>
                {form.veto_enabled
                  ? <li className={form.veto_style ? 'ok' : errors.veto_style ? 'bad' : ''}>{t('createMatch.todoVeto', 'A veto style')}</li>
                  : <li className={mapsDone ? 'ok' : errors.maps ? 'bad' : ''}>{t('createMatch.todoMaps', '{{picked}}/{{total}} maps picked', { picked: mapsPicked, total: bo })}</li>}
              </ul>
            </div>

            <div className="cmp-prev-foot">
              {error && <div className="cmp-error">{error}</div>}
              <button type="button" className="cmp-btn primary big" onClick={submit} disabled={creating}>
                {creating ? <><span className="cmp-spin" />{t('createMatch.creating', 'Creating...')}</> : form.veto_enabled ? t('matches.createVeto', 'Create Veto') : t('nav.createMatch', 'Create Match')}
              </button>
              <Link to="/admin/matches" className="cmp-btn ghost">{t('common.cancel', 'Cancel')}</Link>
              <div className="cmp-kbd"><kbd>Ctrl</kbd> + <kbd>Enter</kbd> {t('createMatch.toCreate', 'to create')}</div>
            </div>
          </div>
        </aside>
      </div>

      {creating && <MatchSetupProgress step={progress.step} label={progress.label} hasBackup={progress.hasBackup} serverInfo={progress.serverInfo} />}
      {vetoResult && (
        <VetoLinksModal vetoLinks={vetoResult.links} team1Name={team1?.name} team2Name={team2?.name} team1={team1} team2={team2}
          onClose={() => navigate(vetoResult.matchId ? `/admin/matches/${vetoResult.matchId}` : '/admin/matches')} />
      )}
      <AlertModal show={!!alert} message={alert || ''} type="info" onClose={() => setAlert(null)} />
    </div>
  );
}
